import React from 'react';
import PropTypes from 'prop-types';

/**
 * ScoreRing — SVG circular progress indicator.
 * Color-coded: green > 70, amber 40–70, red < 40.
 * Renders the score number in the center.
 */
const ScoreRing = ({ score = 0, size = 48, strokeWidth = 4, className = '' }) => {
  const safeScore = Math.max(0, Math.min(100, score ?? 0));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safeScore / 100) * circumference;
  const center = size / 2;

  const getColor = (value) => {
    if (value >= 70) return '#10b981'; // emerald-500
    if (value >= 40) return '#f59e0b'; // amber-500
    return '#ef4444'; // red-500
  };

  const strokeColor = getColor(safeScore);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <span
        className="absolute font-semibold"
        style={{
          fontSize: size * 0.28,
          color: strokeColor,
        }}
      >
        {safeScore}
      </span>
    </div>
  );
};

ScoreRing.propTypes = {
  score: PropTypes.number,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  className: PropTypes.string,
};

export default ScoreRing;
